export type TaxIdInfo = {
  countryEnum: string;
  taxIdName: string;
  example: string;
  countryCode: string;
  regex: string;
};

export function countryTaxIdInfo(
  countryCode?: string
): TaxIdInfo | TaxIdInfo[] | undefined {
  if (!countryCode) return undefined;
  const countries = {
    ad: {
      countryEnum: 'ad_nrt',
      taxIdName: 'Andorran NRT number',
      example: 'A-123456-Z',
      countryCode: 'AD',
      regex: '^(A-\\d{6}-[A-Z])$',
    },
    ar: {
      countryEnum: 'ar_cuit',
      taxIdName: 'Argentinian tax ID number',
      example: '12-3456789-01',
      countryCode: 'AR',
      regex: '^(\\d{2}-\\d{8}-\\d{2})$',
    },
    au: [
      {
        countryEnum: 'au_abn',
        taxIdName: 'ABN',
        example: '123456789123',
        countryCode: 'AU',
        regex: '^(\\d{11})$',
      },
      {
        countryEnum: 'au_arn',
        taxIdName: 'ARN',
        example: '123456789123',
        countryCode: 'AU',
        regex: '^(\\d{12})$',
      },
    ],
    at: {
      countryEnum: 'eu_vat',
      taxIdName: 'EU VAT number',
      example: 'ATU12345678',
      countryCode: 'AT',
      regex: '^(ATU\\d{8})$',
    },
    be: {
      countryEnum: 'eu_vat',
      taxIdName: 'EU VAT number',
      example: 'BE0123456789',
      countryCode: 'BE',
      regex: '^(BE\\d{10})$',
    },
    bh: {
      countryEnum: 'bh_vat',
      taxIdName: 'Bahraini VAT Number',
      example: '123456789012345',
      countryCode: 'BH',
      regex: '^(\\d{15})$',
    },
    bo: {
      countryEnum: 'bo_tin',
      taxIdName: 'Bolivian tax ID',
      example: '123456789',
      countryCode: 'BO',
      regex: '^(\\d{9})$',
    },
    br: [
      {
        countryEnum: 'br_cnpj',
        taxIdName: 'Brazilian CNPJ number',
        example: '01.234.456/5432-10',
        countryCode: 'BR',
        regex: '^(\\d{2}\\.\\d{3}\\.\\d{3}/\\d{4}-\\d{2})$',
      },
      {
        countryEnum: 'br_cpf',
        taxIdName: 'Brazilian CPF number',
        example: '123.456.789-87',
        countryCode: 'BR',
        regex: '^(\\d{3}\\.\\d{3}\\.\\d{3}-\\d{2})$',
      },
    ],
    bg: {
      countryEnum: 'bg_uic',
      taxIdName: 'EU VAT number',
      example: 'BG0123456789',
      countryCode: 'BG',
      regex: '^(BG\\d{9,10})$',
    },
    ca: {
      countryEnum: 'ca_bn',
      taxIdName: 'Canadian Business Number',
      example: '123456789',
      countryCode: 'CA',
      regex: '^(\\d{9})$',
    },
    cl: {
      countryEnum: 'cl_tin',
      taxIdName: 'Chilean TIN',
      example: '12.345.678-K',
      countryCode: 'CL',
      regex: '^(\\d{2}\\.\\d{3}\\.\\d{3}-[A-Z])$',
    },
    cn: {
      countryEnum: 'cn_tin',
      taxIdName: 'Chinese tax ID',
      example: '123456789012345678',
      countryCode: 'CN',
      regex: '^(.*)$',
    },
    co: {
      countryEnum: 'co_nit',
      taxIdName: 'Colombian NIT number',
      example: '123.456.789-0',
      countryCode: 'CO',
      regex: '^(\\d{3}\\.\\d{3}\\.\\d{3}-\\d)$',
    },
    cr: {
      countryEnum: 'cr_tin',
      taxIdName: 'Costa Rican tax ID',
      example: '1-234-567890',
      countryCode: 'CR',
      regex: '^(\\d-\\d{3}-\\d{6})$',
    },
    hr: {
      // Croatia
      countryEnum: 'eu_vat',
      taxIdName: 'EU VAT number',
      example: 'HR12345678912',
      countryCode: 'HR',
      regex: '^(HR\\d{11})$',
    },
    cy: {
      countryEnum: 'eu_vat',
      taxIdName: 'EU VAT number',
      example: 'CY12345678Z',
      countryCode: 'CY',
      regex: '^(CY\\d{8}[A-Z])$',
    },
    cz: {
      countryEnum: 'eu_vat',
      taxIdName: 'EU VAT number',
      example: 'CZ1234567890',
      countryCode: 'CZ',
      regex: '^(CZ\\d{8,10})$',
    },
    do: {
      countryEnum: 'do_rcn',
      taxIdName: 'Dominican RCN number',
      example: '123-4567890-1',
      countryCode: 'DO',
      regex: '^(\\d{3}-\\d{7}-\\d)$',
    },
    dk: {
      countryEnum: 'eu_vat',
      taxIdName: 'EU VAT number',
      example: 'DK12345678',
      countryCode: 'DK',
      regex: '^(DK\\d{8})$',
    },
    ec: {
      countryEnum: 'ec_ruc',
      taxIdName: 'Ecuadorian RUC number',
      example: '1234567890001',
      countryCode: 'EC',
      regex: '^(\\d{13})$',
    },
    ee: {
      countryEnum: 'eu_vat',
      taxIdName: 'EU VAT number',
      example: 'EE123456789',
      countryCode: 'EE',
      regex: '^(EE\\d{9})$',
    },
    eg: {
      countryEnum: 'eg_tin',
      taxIdName: 'Egyptian Tax Id Number',
      example: '123456789',
      countryCode: 'EG',
      regex: '^(\\d{9})$',
    },
    sv: {
      countryEnum: 'sv_nit',
      taxIdName: 'El Salvadorian NIT number',
      example: '1234-567890-123-4',
      countryCode: 'SV',
      regex: '^(\\d{4}-\\d{6}-\\d{3}-\\d)$',
    },
    fi: {
      countryEnum: 'eu_vat',
      taxIdName: 'EU VAT number',
      example: 'FI12345678',
      countryCode: 'FI',
      regex: '^(FI\\d{8})$',
    },
    fr: {
      countryEnum: 'eu_vat',
      taxIdName: 'EU VAT number',
      example: 'FRAB123456789',
      countryCode: 'FR',
      regex: '^(FR[A-Z0-9]{2}\\d{9})$',
    },
    ge: {
      countryEnum: 'ge_vat',
      taxIdName: 'Georgian VAT',
      example: '123456789',
      countryCode: 'GE',
      regex: '^(\\d{9})$',
    },
    de: [
      {
        countryEnum: 'eu_vat',
        taxIdName: 'EU VAT Number',
        example: 'DE123456789',
        countryCode: 'DE',
        regex: '^(DE\\d{9})$',
      },
      {
        countryEnum: 'de_stn',
        taxIdName: 'Steuernummer',
        example: '1234567890',
        countryCode: 'DE',
        regex: '^(\\d{10})$',
      },
    ],
    gr: {
      countryEnum: 'eu_vat',
      taxIdName: 'EU VAT number',
      example: 'EL123456789',
      countryCode: 'GR',
      regex: '^(EL\\d{9})$',
    },
    hk: {
      countryEnum: 'hk_br',
      taxIdName: 'Hong Kong BR number',
      example: '12345678',
      countryCode: 'HK',
      regex: '^(\\d{8})$',
    },
    hu: [
      {
        countryEnum: 'eu_vat',
        taxIdName: 'EU VAT number',
        example: 'HU12345678',
        countryCode: 'HU',
        regex: '^(HU\\d{8})$',
      },
      {
        countryEnum: 'hu_tin',
        taxIdName: 'Hungary tax number (ad\u00f3sz\u00e1m)',
        example: '12345678-1-23',
        countryCode: 'HU',
        regex: '^(\\d{8}-\\d-\\d{2})$',
      },
    ],
    is: {
      countryEnum: 'is_vat',
      taxIdName: 'Icelandic VAT',
      example: '123456',
      countryCode: 'IS',
      regex: '^(\\d{6})$',
    },
    in: {
      countryEnum: 'in_gst',
      taxIdName: 'Indian GST number',
      example: '12ABCDE3456FGZH',
      countryCode: 'IN',
      regex: '^(\\d{2}[A-Z0-9]{10}[A-Z0-9]{1}Z[A-Z0-9]{1})$',
    },
    id: {
      countryEnum: 'id_npwp',
      taxIdName: 'Indonesian NPWP number',
      example: '12.345.678.9-012.345',
      countryCode: 'ID',
      regex: '^(\\d{2}\\.\\d{3}\\.\\d{3}\\.\\d-\\d{3}\\.\\d{3})$',
    },
    ie: {
      countryEnum: 'eu_vat',
      taxIdName: 'EU VAT number',
      example: 'IE1234567AB',
      countryCode: 'IE',
      regex: '^(IE[A-Z0-9]{8,9})$',
    },
    il: {
      countryEnum: 'il_vat',
      taxIdName: 'Israel VAT',
      example: '000012345',
      countryCode: 'IL',
      regex: '^(\\d{9})$',
    },
    it: {
      countryEnum: 'eu_vat',
      taxIdName: 'EU VAT number',
      example: 'IT12345678912',
      countryCode: 'IT',
      regex: '^(IT\\d{11})$',
    },
    jp: [
      {
        countryEnum: 'jp_cn',
        taxIdName: 'Japanese Corporate Number',
        example: '1234567891234',
        countryCode: 'JP',
        regex: '^(\\d{13})$',
      },
      {
        countryEnum: 'jp_rn',
        taxIdName: 'Japanese registered foreign business number',
        example: '12345',
        countryCode: 'JP',
        regex: '^(\\d{5})$',
      },
      {
        countryEnum: 'jp_trn',
        taxIdName: 'Japanese Tax Registration Number',
        example: 'T1234567891234',
        countryCode: 'JP',
        regex: '^(T\\d{13})$',
      },
    ],
    kz: {
      countryEnum: 'kz_bin',
      taxIdName: 'Kazakhstani Business Id Number',
      example: '123456789012',
      countryCode: 'KZ',
      regex: '^(\\d{12})$',
    },
    ke: {
      countryEnum: 'ke_pin',
      taxIdName: 'Kenya Revenue Authority Personal Id Number',
      example: 'P000111111A',
      countryCode: 'KE',
      regex: '^(P\\d{9}[A-Z])$',
    },
    li: {
      countryEnum: 'li_uid',
      taxIdName: 'Liechtensteinian UID number',
      example: 'CHE123456789',
      countryCode: 'LI',
      regex: '^(CHE\\d{9})$',
    },
    lv: {
      countryEnum: 'eu_vat',
      taxIdName: 'EU VAT number',
      example: 'LV12345678912',
      countryCode: 'LV',
      regex: '^(LV\\d{11})$',
    },
    lt: {
      countryEnum: 'eu_vat',
      taxIdName: 'EU VAT number',
      example: 'LT123456789123',
      countryCode: 'LT',
      regex: '^(LT\\d{9}|LT\\d{12})$',
    },
    lu: {
      countryEnum: 'eu_vat',
      taxIdName: 'EU VAT number',
      example: 'LU12345678',
      countryCode: 'LU',
      regex: '^(LU\\d{8})$',
    },
    mt: {
      countryEnum: 'eu_vat',
      taxIdName: 'EU VAT number',
      example: 'MT12345678',
      countryCode: 'MT',
      regex: '^(MT\\d{8})$',
    },
    my: [
      {
        countryEnum: 'my_itn',
        taxIdName: 'Malaysian ITN number',
        example: '	C 1234567890',
        countryCode: 'MY',
        regex: '^(\\d{11})$',
      },
      {
        countryEnum: 'my_sst',
        taxIdName: 'Malaysian SST number',
        example: 'A12-3456-78912345',
        countryCode: 'MY',
        regex: '^[A-Z0-9]{3}-\\d{4}-[0-9A-Z]{8}$',
      },
    ],
    mx: {
      countryEnum: 'mx_rfc',
      taxIdName: 'Mexican RFC number',
      example: 'ABC010203AB9',
      countryCode: 'MX',
      regex: '^[A-Z]{3}\\d{6}[0-9A-Z]{3}$',
    },
    nl: {
      countryEnum: 'eu_vat',
      taxIdName: 'EU VAT number',
      example: 'NL123456789B12',
      countryCode: 'NL',
      regex: '^(NL\\d{9}B\\d{2})$',
    },
    nz: {
      countryEnum: 'nz_gst',
      taxIdName: 'New Zealand GST number',
      example: '123456789',
      countryCode: 'NZ',
      regex: '^[0-9]{9}$',
    },
    ng: {
      countryEnum: 'ng_tin',
      taxIdName: 'Nigerian Tax Id Number',
      example: '12345678-0001',
      countryCode: 'NG',
      regex: '^(\\d{8}-\\d{4})$',
    },
    no: [
      {
        countryEnum: 'no_vat',
        taxIdName: 'Norwegian VAT number',
        example: '123456789MVA',
        countryCode: 'NO',
        regex: '^(\\d{9}[A-Z]{3})$',
      },
      {
        countryEnum: 'no_voec',
        taxIdName: 'Norwegian VAT e-commerce number',
        example: '1234567',
        countryCode: 'NO',
        regex: '^(\\d{7})$',
      },
    ],
    om: {
      countryEnum: 'om_vat',
      taxIdName: 'Omani VAT Number',
      example: 'OM1234567890',
      countryCode: 'OM',
      regex: '^(OM\\d{10})$',
    },
    pe: {
      countryEnum: 'pe_ruc',
      taxIdName: 'Peruvian RUC number',
      example: '12345678901',
      countryCode: 'PE',
      regex: '^(\\d{11})$',
    },
    ph: {
      countryEnum: 'ph_tin',
      taxIdName: 'Philippines Tax Id Number',
      example: '123456789012',
      countryCode: 'PH',
      regex: '^(\\d{12})$',
    },
    pl: {
      countryEnum: 'eu_vat',
      taxIdName: 'EU VAT number',
      example: 'PL1234567890',
      countryCode: 'PL',
      regex: '^(PL\\d{10})$',
    },
    pt: {
      countryEnum: 'eu_vat',
      taxIdName: 'EU VAT number',
      example: 'PT123456789',
      countryCode: 'PT',
      regex: '^(PT\\d{9})$',
    },
    ro: [
      {
        countryEnum: 'ro_tin',
        taxIdName: 'Romanian tax ID number',
        example: 'RO1234567890',
        countryCode: 'RO',
        regex: '^(RO\\d{2,10})$',
      },
      {
        countryEnum: 'ro_vat',
        taxIdName: 'Romanian VAT number',
        example: 'RO1234567890',
        countryCode: 'RO',
        regex: '^(RO\\d{2,10})$',
      },
    ],
    ru: {
      countryEnum: 'ru_kpp',
      taxIdName: 'Russian KPP',
      example: '123456789',
      countryCode: 'RU',
      regex: '^(\\d{9})$',
    },
    sa: {
      countryEnum: 'sa_vat',
      taxIdName: 'Saudi Arabia VAT',
      example: '123456789012345',
      countryCode: 'SA',
      regex: '^(\\d{15})$',
    },
    rs: {
      countryEnum: 'rs_pib',
      taxIdName: 'Serbian PIB number',
      example: '123456789',
      countryCode: 'RS',
      regex: '^(\\d{9})$',
    },
    sg: [
      {
        countryEnum: 'sg_gst',
        taxIdName: 'Singaporean GST',
        example: 'M12345678X',
        countryCode: 'SG',
        regex: '^(M[A-Z0-9]\\d{8}[A-Z])$',
      },
      {
        countryEnum: 'sg_uen',
        taxIdName: 'Singaporean UEN',
        example: '53480099U',
        countryCode: 'SG',
        regex: '^(\\d{9}[A-Z])$',
      },
    ],
    sk: {
      countryEnum: 'eu_vat',
      taxIdName: 'EU VAT number',
      example: 'SK1234567891',
      countryCode: 'SK',
      regex: '^(SK\\d{10})$',
    },
    si: {
      countryEnum: 'eu_vat',
      taxIdName: 'EU VAT number',
      example: 'SI12345678',
      countryCode: 'SI',
      regex: '^(SI\\d{8})$',
    },
    za: {
      countryEnum: 'za_vat',
      taxIdName: 'South African VAT number',
      example: '4123456789',
      countryCode: 'ZA',
      regex: '^(\\d{10})$',
    },
    kr: {
      countryEnum: 'kr_brn',
      taxIdName: 'Korean BRN',
      example: '123-45-67890',
      countryCode: 'KR',
      regex: '^(\\d{3})-(\\d{2})-(\\d{5})$',
    },
    es: {
      countryEnum: 'eu_vat',
      taxIdName: 'EU VAT number',
      example: 'ESA12345678',
      countryCode: 'ES',
      regex: '^(ES[A-Z0-9]{9})$',
    },
    ch: [
      {
        countryEnum: 'ch_uid',
        taxIdName: 'Switzerland UID number',
        example: 'CHE-123.456.789 HR',
        countryCode: 'CH',
        regex: '^(.*)$',
      },
      {
        countryEnum: 'ch_vat',
        taxIdName: 'Switzerland VAT number',
        example: 'CHE-123.456.789 MWST',
        countryCode: 'CH',
        regex: '^(.*)$',
      },
    ],
    tw: {
      countryEnum: 'tw_vat',
      taxIdName: 'Taiwanese VAT',
      example: '12345678',
      countryCode: 'TW',
      regex: '^(\\d{8})$',
    },
    th: {
      countryEnum: 'th_vat',
      taxIdName: 'Thai VAT',
      example: '123456789123',
      countryCode: 'TH',
      regex: '^(\\d{13})$',
    },
    tr: {
      countryEnum: 'tr_tin',
      taxIdName: 'Turkish Tax Id Number',
      example: '0123456789',
      countryCode: 'TR',
      regex: '^(\\d{10})$',
    },
    ua: {
      countryEnum: 'ua_vat',
      taxIdName: 'Ukrainian VAT',
      example: '123456789',
      countryCode: 'UA',
      regex: '^(\\d{9})$',
    },
    ae: {
      countryEnum: 'ae_trn',
      taxIdName: 'United Arab Emirates TRN',
      example: '123456789012345',
      countryCode: 'AE',
      regex: '^(\\d{15})$',
    },
    gb: {
      countryEnum: 'gb_vat',
      taxIdName: 'United Kingdom VAT number',
      example: 'GB123456789',
      countryCode: 'GB',
      regex: '^(GB\\d{9,12})$',
    },
    uy: {
      countryEnum: 'uy_ruc',
      taxIdName: 'Uruguayan RUC number',
      example: '123456789012',
      countryCode: 'UY',
      regex: '^(\\d{12})$',
    },
    ve: {
      countryEnum: 've_rif',
      taxIdName: 'Venezuelan RIF number',
      example: 'A-12345678-9',
      countryCode: 'VE',
      regex: '^(A-\\d{8}-9)$',
    },
    vn: {
      countryEnum: 'vn_tin',
      taxIdName: 'Vietnamese tax ID number',
      example: '1234567890',
      countryCode: 'VN',
      regex: '^(\\d{10})$',
    },
  };

  return countries[countryCode.toLowerCase()];
}

export const getSingleTaxId = (
  countryCode?: string,
  taxIdType?: string
): TaxIdInfo | undefined => {
  const taxIdOrList = countryTaxIdInfo(countryCode);

  if (Array.isArray(taxIdOrList)) {
    if (taxIdType) {
      return taxIdOrList.find(
        (item) => item.countryEnum === taxIdType
      ) as TaxIdInfo;
    }
    return undefined;
  }
  return taxIdOrList as TaxIdInfo;
};
